import { useState, useEffect, useCallback } from 'react';
import { logger } from '../utils/logger';

/**
 * Global token cache to prevent duplicate requests across components
 */
const globalTokenCache = {
  token: null,
  timestamp: null,
  expiresIn: 50 * 60 * 1000, // 50 minutes (tokens expire after 1 hour)
  pendingRequest: null, // Track ongoing requests to prevent duplicates
};

/**
 * Custom hook for managing CSRF tokens
 * Handles token fetching, caching, and automatic refresh
 */
const useCSRF = () => {
  const [csrfToken, setCsrfToken] = useState(globalTokenCache.token);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch a new CSRF token from the server
   */
  const fetchToken = useCallback(async () => {
    // Check if we have a valid cached token
    if (globalTokenCache.token && globalTokenCache.timestamp) {
      const now = Date.now();
      if (now - globalTokenCache.timestamp < globalTokenCache.expiresIn) {
        setCsrfToken(globalTokenCache.token);
        return globalTokenCache.token;
      }
    }

    // Check if there's already a pending request
    if (globalTokenCache.pendingRequest) {
      logger.debug('CSRF token request already in progress, waiting...');
      return await globalTokenCache.pendingRequest;
    }

    setIsLoading(true);
    setError(null);

    // Create a promise for the request and store it to prevent duplicates
    globalTokenCache.pendingRequest = (async () => {
      try {
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
        const response = await fetch(`${apiBaseUrl}/api/csrf-token.php`, {
          method: 'GET',
          credentials: 'include', // Include cookies for session management
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch CSRF token: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();

        if (result.success && result.csrf_token) {
          // Cache the token globally
          globalTokenCache.token = result.csrf_token;
          globalTokenCache.timestamp = Date.now();
          
          setCsrfToken(result.csrf_token);
          return result.csrf_token;
        } else {
          throw new Error(result.message || 'Invalid CSRF token response');
        }
      } catch (err) {
        logger.error('Error fetching CSRF token:', err);
        setError(err.message);
        throw err;
      } finally {
        setIsLoading(false);
        globalTokenCache.pendingRequest = null; // Clear the pending request
      }
    })();

    return await globalTokenCache.pendingRequest;
  }, []);

  /**
   * Get a valid CSRF token (fetch if needed)
   */
  const getToken = useCallback(async () => {
    if (csrfToken) {
      return csrfToken;
    }
    return await fetchToken();
  }, [csrfToken, fetchToken]);

  /**
   * Refresh the CSRF token
   */
  const refreshToken = useCallback(async () => {
    globalTokenCache.token = null;
    globalTokenCache.timestamp = null;
    setCsrfToken(null);
    return await fetchToken();
  }, [fetchToken]);

  /**
   * Make an authenticated API request with CSRF token
   */
  const makeAuthenticatedRequest = useCallback(async (url, options = {}) => {
    try {
      const token = await getToken();
      
      // Prepare the request
      const requestOptions = {
        ...options,
        credentials: 'include', // Include cookies for session management
        headers: {
          ...options.headers,
        },
      };

      // Add CSRF token based on content type
      if (options.body instanceof FormData) {
        // For FormData (multipart/form-data), append the token
        options.body.append('csrf_token', token);
      } else if (options.headers && options.headers['Content-Type'] === 'application/json') {
        // For JSON requests, add to the body
        const bodyData = options.body ? JSON.parse(options.body) : {};
        bodyData.csrf_token = token;
        requestOptions.body = JSON.stringify(bodyData);
      } else {
        // For other content types, add as header
        requestOptions.headers['X-CSRF-Token'] = token;
      }

      const response = await fetch(url, requestOptions);

      // If we get a 403 error, the token might be invalid, try to refresh
      if (response.status === 403) {
        logger.warn('CSRF token validation failed, refreshing token...');
        
        // Clear the current token and fetch a new one
        globalTokenCache.token = null;
        globalTokenCache.timestamp = null;
        setCsrfToken(null);
        
        const newToken = await fetchToken();
        
        // Retry the request with the new token
        if (options.body instanceof FormData) {
          options.body.set('csrf_token', newToken);
        } else if (options.headers && options.headers['Content-Type'] === 'application/json') {
          const bodyData = options.body ? JSON.parse(options.body) : {};
          bodyData.csrf_token = newToken;
          requestOptions.body = JSON.stringify(bodyData);
        } else {
          requestOptions.headers['X-CSRF-Token'] = newToken;
        }
        
        return await fetch(url, requestOptions);
      }

      return response;
    } catch (err) {
      logger.error('Error making authenticated request:', err);
      throw err;
    }
  }, [getToken, fetchToken]);

  // Fetch initial token on mount
  useEffect(() => {
    fetchToken().catch(err => {
      logger.warn('Failed to fetch initial CSRF token:', err);
    });
  }, [fetchToken]);

  return {
    csrfToken,
    isLoading,
    error,
    getToken,
    refreshToken,
    makeAuthenticatedRequest,
  };
};

export default useCSRF;
